import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { RenterService } from '../../../core/services';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';
import {
  combineBangladeshMobileNumber,
  extractBangladeshMobileLocalPart,
  normalizeBangladeshMobileLocalInput,
  BANGLADESH_COUNTRY_CODE
} from '../../../core/utils/bd-mobile.util';

export interface RenterSaveEvent {
  mode: 'create' | 'edit';
  refId?: number;
  renterName?: string;
}

@Component({
  selector: 'app-add-edit-renter',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './add-edit-renter.component.html',
  styleUrls: ['./add-edit-renter.component.css', './../../../../CSSFiles/BTN_Style.css']
})
export class AddEditRenterComponent implements OnInit {
  readonly mobilePrefix = BANGLADESH_COUNTRY_CODE;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() initialRenter: Record<string, any> | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<RenterSaveEvent>();
  @Output() editDocuments = new EventEmitter<void>();

  renterForm: FormGroup;
  isSubmitting = false;
  error: string | null = null;

  constructor(
    private fb: FormBuilder,
    private readonly renters: RenterService
  ) {
    this.renterForm = this.fb.group({
      renterName: ['', Validators.required],
      renterMobile: ['', [Validators.required, Validators.pattern(/^1\d{9}$/)]],
      renterPhotoIdNo: [''],
      renterPermanentAddress: ['', Validators.required],
      renterPreviousAddress: ['', Validators.required],
      renterEmail: ['', Validators.email]
    });
  }

  ngOnInit(): void {
    if (this.mode === 'edit' && this.initialRenter) {
      this.renterForm.patchValue({
        renterName: this.initialRenter['renterName'] ?? '',
        renterMobile: extractBangladeshMobileLocalPart(this.initialRenter['renterMobile']),
        renterPhotoIdNo: this.initialRenter['renterPhotoIdNo'] ?? '',
        renterPermanentAddress: this.initialRenter['renterPermanentAddress'] ?? '',
        renterPreviousAddress: this.initialRenter['renterPreviousAddress'] ?? '',
        renterEmail: this.initialRenter['renterEmail'] ?? ''
      });
    }
  }

  closeModal(): void {
    this.close.emit();
  }

  openDocumentUpdate(): void {
    if (this.mode !== 'edit') {
      return;
    }
    this.editDocuments.emit();
  }

  normalizeMobileInput(): void {
    const control = this.renterForm.get('renterMobile');
    control?.setValue(normalizeBangladeshMobileLocalInput(control.value), { emitEvent: false });
  }

  onSubmit(): void {
    this.normalizeMobileInput();
    if (this.renterForm.invalid) {
      this.renterForm.markAllAsTouched();
      return;
    }

    const payload: Record<string, any> = {
      ...this.renterForm.value,
      renterName: `${this.renterForm.value['renterName'] ?? ''}`.trim(),
      renterMobile: combineBangladeshMobileNumber(this.renterForm.value['renterMobile']),
      renterPhotoIdNo: `${this.renterForm.value['renterPhotoIdNo'] ?? ''}`.trim(),
      renterPermanentAddress: `${this.renterForm.value['renterPermanentAddress'] ?? ''}`.trim(),
      renterPreviousAddress: `${this.renterForm.value['renterPreviousAddress'] ?? ''}`.trim(),
      renterEmail: `${this.renterForm.value['renterEmail'] ?? ''}`.trim()
    };

    if (this.mode === 'edit') {
      const id = Number(this.initialRenter?.['id']);
      if (!Number.isFinite(id)) {
        this.error = 'Renter id is missing for update.';
        return;
      }
      payload['id'] = id;
    }

    this.isSubmitting = true;
    this.error = null;

    const request$ = this.mode === 'edit'
      ? this.renters.update(payload)
      : this.renters.create(payload);

    request$
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (res: any) => {
          const refId = Number(res?.refId ?? (this.mode === 'edit' ? this.initialRenter?.['id'] : undefined));
          this.save.emit({
            mode: this.mode,
            refId: Number.isFinite(refId) ? refId : undefined,
            renterName: payload['renterName']
          });
        },
        error: (err: any) => {
          this.error = err?.message || `Failed to ${this.mode === 'edit' ? 'update' : 'create'} renter.`;
        }
      });
  }
}
